import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ProfileLink } from '../ngrx-store/app.state'
import { BehaviorSubject, Observable, Subject, forkJoin } from 'rxjs'
import { distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { PushNotificationService } from '../services/push-notification.service'
import { HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http'
import { profileUpdateSuccess } from '../ngrx-store/actions/corp-member.actions'
import { ActivatedRoute } from '@angular/router'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import * as Sentry from "@sentry/angular";
import { Clipboard } from '@angular/cdk/clipboard'
import { isPublicProfileEnabled } from '../utils/corp-member.util'
import { NAME_MAX_LENGTH } from '../utils/constants.util'
import { LgaPpa, PpaDirectoryService } from '../services/ppa-directory.service'
import { SimilarPpa } from '../dialogs/similar-ppas-dialog/similar-ppas-dialog.component'

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit, OnDestroy {
    readonly nameMaxLength = NAME_MAX_LENGTH

    constructor(
        private activatedRoute: ActivatedRoute,
        public callerService: CallerService,
        private clipboard: Clipboard,
        private pushNotificationService: PushNotificationService,
        private ppaDirectory: PpaDirectoryService,
    ) {}
    @ViewChild('profilePhotoInput', { read: ElementRef<HTMLInputElement> })
        profilePhotoInput!: ElementRef<HTMLInputElement>

    /** Why the toggle can't work here, if it can't - shown instead of hiding the setting. */
    pushAvailability = this.pushNotificationService.availability()
    get pushUnavailableHelp(): string | null {
        return this.pushNotificationService.unavailableHelp()
    }
    pushEnabled$ = this.pushNotificationService.isSubscribed()


    INVITE_TEXT = `Join me on corpers.ng! Sign up with my referral code ${window.location.origin}/signup?ref=${this.callerService.corpMember.slug_id}`
    filteredTypeOfInstitutionOptions: Observable<string[]> | undefined

    stateCodeInputDisabled = false

    isUpdatingProfile = false
    isGettingLocation = false

    ng_states: any[] = []
    ng_states_and_lgas: any[] = []
    selected_state_lgas: any[] = []

    /** The LGA's PPAs, for the PPA autocomplete (PpaDirectoryService) - they follow the LGA field. */
    PPAsInSelectedLGA: LgaPpa[] = []
    private ppasInSelectedLga$ = new BehaviorSubject<LgaPpa[]>([])
    private lgaForPpas$ = new Subject<string | number>()
    filteredLgaPPAs: Observable<LgaPpa[]> | undefined;

    private destroy$ = new Subject<void>()

    serviceDetailsFormGroup: FormGroup = new FormGroup({
        signed_up_as_prospective_corp_member: new FormControl(false),
        service_state: new FormControl(
            this.callerService.corpMember?.service_state,
            [Validators.required],
        ),
        lga_id: new FormControl(this.callerService.corpMember?.lga_id, [
            Validators.required,
        ]),
        city_or_town: new FormControl(
            this.callerService.corpMember?.city_or_town,
        ),
        stream: new FormControl(this.callerService.corpMember?.stream),
        state_code: new FormControl(this.callerService.corpMember?.state_code, [
            Validators.required,
            Validators.pattern(this.callerService.state_code_regex),
        ]),
    })

    personalDetailsFormGroup: FormGroup = new FormGroup({
        first_name: new FormControl(this.callerService.corpMember?.first_name, Validators.maxLength(NAME_MAX_LENGTH)),
        last_name: new FormControl(this.callerService.corpMember?.last_name, Validators.maxLength(NAME_MAX_LENGTH)),
        nickname: new FormControl(this.callerService.corpMember?.nickname),
    })

    otherDetailsFormGroup: FormGroup = new FormGroup({
        travel_from_state: new FormControl(
            this.callerService.corpMember.travel_from_state,
        ),
        where_they_found_accommodation: new FormControl(null),
        travel_from_city: new FormControl(
            this.callerService.corpMember.travel_from_city,
        ),
        want_spa_or_not: new FormControl(
            this.callerService.corpMember.want_spa_or_not,
        ),
        looking_for_accommodation_or_not: new FormControl(
            this.callerService.corpMember.looking_for_accommodation_or_not,
        ),
    })

    ppaDetailsFormGroup: FormGroup = new FormGroup({
        ppa_name: new FormControl(this.callerService.corpMember.PPA?.name),
        type_of_institution: new FormControl(this.callerService.corpMember.PPA?.type_of_ppa),
        
        // we'll pick the one you selected/added - the api returns just once associated location (by the user)
        ppa_id: new FormControl(this.callerService.corpMember.PPA?.id),
        ppa_address: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.address),
        ppa_directions: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.directions),
        location_id: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.id),
        ppa_lat: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.lat ?? null),
        ppa_lng: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.lng ?? null),
    }, { validators: [ this.requireBothFieldsValidator('ppa_name', 'type_of_institution') ] })

    bioDetailsFormGroup: FormGroup = new FormGroup({
        public_profile: new FormControl(
            this.callerService.corpMember?.public_profile,
        ),
        nickname: new FormControl(this.callerService.corpMember?.nickname),
        bio: new FormControl(this.callerService.corpMember?.bio),
    })

    // combo form
    profileFormGroup: FormGroup = new FormGroup({
        bioDetails: this.bioDetailsFormGroup,
        ppaDetails: this.ppaDetailsFormGroup,
        otherDetails: this.otherDetailsFormGroup,
        serviceDetails: this.serviceDetailsFormGroup,
        personalDetails: this.personalDetailsFormGroup,
    })

    serviceStreams: Array<string> = ['1', '2', '3']

    /**
     * if control1 is filled, and control2 is not, then control2 is required
     * if control2 is filled, and control1 is not, then control1 is required
     * if control1 and control2 is not filled, clear errors
     * @param field1 
     * @param field2 
     * @returns 
     * 
     * from chat gpt
     */
    requireBothFieldsValidator(field1: string, field2: string): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
          
            const control1 = group.get(field1);
            const control2 = group.get(field2);
        
            if (!control1 || !control2) return null; // Safety check
        
            const value1 = control1.value;
            const value2 = control2.value;

            if (!value1 && !value2) {
                if (control1?.errors?.['required']) {
                    delete control1?.errors?.['required'];
                    control1.updateValueAndValidity();
                }

                if (control2?.errors?.['required']) {
                    delete control2?.errors?.['required'];
                    control2.updateValueAndValidity();
                }
                
                return null
            }
        
            if (value1 && !value2) {
                // If either is filled and the other is not, error
                control2.setErrors({ required: true });
                return { required: true };
            }

            if (value2 && !value1) {
                // If either is filled and the other is not, error
                control1.setErrors({ required: true });
                return { required: true };
            }

            if (control1?.value && control1?.errors?.['required']) {
                delete control1?.errors?.['required'];
                control1.updateValueAndValidity();
            }

            if (control2?.value && control2?.errors?.['required']) {
                delete control2?.errors?.['required'];
                control2.updateValueAndValidity();
            }
        
            return null; // Valid
        };
      }

    ngOnInit(): void {
        this.activatedRoute.data.subscribe(({ res }) => {
            // do something with your resolved data ...
            console.log('2nd resolved data', res)

            if (res.status === HttpStatusCode.Ok) {
                this.ng_states_and_lgas = res.body?.states_and_lgas

                // This should be string options.
                this.ng_states = this.ng_states_and_lgas.map((s, i) => ({
                    index: i,
                    id: s.id,
                    name: s.name,
                }))
            }
        })

        this.serviceDetailsFormGroup.valueChanges.subscribe({
            next: (values) => {
                // console.log('service details values', values);
            },
        })

        // Subscribe to prospective corp member checkbox changes
        this.serviceDetailsFormGroup
            .get('signed_up_as_prospective_corp_member')
            ?.valueChanges.subscribe({
                next: (isProspective) => {
                    this.toggleServiceDetailsControls(isProspective)
                    this.togglePPADetailsControls(isProspective)
                },
            })

        this.serviceDetailsFormGroup
            .get(['service_state'])
            ?.valueChanges.subscribe({
                next: (values) => {
                    console.log('new service state', values)
                    // clear lga value on service state change.
                    this.serviceDetailsFormGroup.get(['lga'])?.setValue('')
                },
            })

        this.bioDetailsFormGroup.valueChanges.subscribe({
            next: (values) => {
                // console.log('values', values);
            },
        })

        this.ppaDetailsFormGroup?.get('ppa_name')?.valueChanges.subscribe({
            next: (value) => {
                // If the user edits the field manually (not via autocomplete selection),
                // clear ppa_id so we don't link them to the wrong existing PPA.
                const currentPpaId = this.ppaDetailsFormGroup.get('ppa_id')?.value
                if (currentPpaId) {
                    const linkedPpa = this.PPAsInSelectedLGA.find((p: any) => p.id === currentPpaId)
                    if (!linkedPpa || linkedPpa.name !== value) {
                        this.ppaDetailsFormGroup.get('ppa_id')?.setValue(null, { emitEvent: false })
                    }
                }
            },
        })

        this.lgaForPpas$
            .pipe(
                startWith(this.callerService.corpMember.lga_id),
                distinctUntilChanged(),
                switchMap((lgaId) => this.ppaDirectory.ppasInLga(lgaId).pipe(startWith([]))), // not the last LGA's while it loads
                takeUntil(this.destroy$),
            )
            .subscribe((ppas) => {
                this.PPAsInSelectedLGA = ppas
                this.ppasInSelectedLga$.next(ppas)
            })
        // Subscribed only while the panel is open (mat-autocomplete renders its options lazily) -
        // hence the subject above, which loads the list as soon as the page opens.
        this.filteredLgaPPAs = this.ppaDirectory.suggestions(
            this.ppasInSelectedLga$,
            this.ppaDetailsFormGroup.get('ppa_name')!.valueChanges.pipe(map((value) => this.displayPpaName(value))),
        )

        this.serviceDetailsFormGroup.get('lga_id')?.valueChanges.subscribe({
            next: (lgaId) => {
                if (!lgaId) return
                // Clear stale PPA selections when the LGA changes
                this.ppaDetailsFormGroup.get('ppa_name')?.reset()
                this.ppaDetailsFormGroup.get('ppa_id')?.reset()
                this.ppaDetailsFormGroup.get('type_of_institution')?.reset()
                this.lgaForPpas$.next(lgaId)
            },
        })

      this.filteredTypeOfInstitutionOptions = this.ppaDetailsFormGroup.get(['type_of_institution'])
        ?.valueChanges.pipe(
            startWith(''),
            map(value => this._filterTypeOfInstitutionOptions(value || '')),
        );

        console.log('hey, this is me', this.callerService.corpMember)

        // Initialize the state of service details controls based on the current checkbox value
        const isProspective = this.serviceDetailsFormGroup.get('signed_up_as_prospective_corp_member')?.value
        this.toggleServiceDetailsControls(isProspective)
        this.togglePPADetailsControls(isProspective)
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    copyInviteText() {
        const copied = this.clipboard.copy(this.INVITE_TEXT)
        if (copied) {
            this.callerService.showNotification('Invite text copied to clipboard. Share with your friends!')
        }
    }

    async onPushToggleChange(enabled: boolean): Promise<void> {
        if (enabled) {
            await this.pushNotificationService.requestPermissionAndSubscribe()
        } else {
            await this.pushNotificationService.unsubscribe()
        }

        // requestPermissionAndSubscribe()/unsubscribe() swallow their own errors (eg. permission denied)
        // and show their own error toast, so only show the success toast if the toggle's outcome
        // actually matches what was requested.
        const isNowSubscribed = await this.pushNotificationService.getCurrentSubscription().pipe(take(1)).toPromise().then((sub) => !!sub)
        this.pushAvailability = this.pushNotificationService.availability() // e.g. they just clicked "Block"

        if (isNowSubscribed === enabled) {
            this.callerService.showNotification(enabled ? 'Notifications enabled' : 'Notifications disabled')
        }
    }

    usePPAGeolocation() {
        if (!navigator.geolocation) {
            this.callerService.showNotification('Geolocation is not supported by your browser')
            return
        }
        this.isGettingLocation = true
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords
                this.ppaDetailsFormGroup.get('ppa_lat')?.setValue(latitude)
                this.ppaDetailsFormGroup.get('ppa_lng')?.setValue(longitude)
                fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`)
                    .then(res => res.json())
                    .then(data => {
                        if (data?.display_name) {
                            this.ppaDetailsFormGroup.get('ppa_address')?.setValue(data.display_name)
                        }
                        this.isGettingLocation = false
                    })
                    .catch(() => {
                        this.isGettingLocation = false
                        this.callerService.showNotification('Location captured. Please type the address manually.')
                    })
            },
            (error) => {
                this.isGettingLocation = false
                const msg = error.code === error.PERMISSION_DENIED
                    ? 'Location permission denied. Please allow location access and try again.'
                    : 'Could not get your location. Please type the address manually.'
                this.callerService.showNotification(msg)
            },
            { timeout: 10000 },
        )
    }

    ppaSelected(event: MatAutocompleteSelectedEvent) {
        const ppa = this.PPAsInSelectedLGA.find((ppaOption) => String(ppaOption.id) === String(event.option.id))
        if (ppa) {
            this.ppaDetailsFormGroup.get('ppa_id')?.setValue(ppa.id, { emitEvent: false })
            this.ppaDetailsFormGroup.get('type_of_institution')?.setValue(ppa?.type_of_ppa)
        }
    }

    displayPpaName(value: any) {
        if (typeof value === 'object') {
            return value?.name
        }
        // else it's string
        return value
    }

    private _filterTypeOfInstitutionOptions(value: string): string[] {
        return CallerService.PPA_TYPE_OPTIONS
        .filter((option: string) => option?.toLowerCase().includes(value.toLowerCase()));
    }

    setSelectedStatesLGA(event: any, value: any) {
        this.selected_state_lgas =
            this.ng_states_and_lgas?.[value.index]?.StateLGAs
    }

    toggleStateCodeInputDisabled() {
        this.stateCodeInputDisabled = !this.stateCodeInputDisabled
    }

    toggleServiceDetailsControls(isProspective: boolean) {
        Object.entries(this.serviceDetailsFormGroup.controls).forEach(([key, control]) => {
            
            // toggle all controls except the signed_up_as_prospective_corp_member control
            if (key === 'signed_up_as_prospective_corp_member') {
                return
            }

            if (isProspective) {
                control.disable()
            } else {
                control.enable()
            }
        })
    
    }

    togglePPADetailsControls(isProspective: boolean) {
        Object.values(this.ppaDetailsFormGroup.controls).forEach((control) => {
            
            // toggle all controls

            if (isProspective) {
                control.disable()
            } else {
                control.enable()
            }
        })
    
    }

    /**
     * Depreciated
     *
     * Missing personalDetailsFormGroup values
     */
    updateCorpMemberProfile(): void {
        console.log('clicked Update!')

        // check this.serviceDetailsFormGroup.pristine
        if (this.serviceDetailsFormGroup.pristine) {
            this.callerService
                .updateProfile(this.serviceDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated updateProfile', res)
                    },
                    error: (err: HttpErrorResponse) => {
                        Sentry.captureException(err.error);
                        this.callerService.showNotification(
                            'Failed to update Profile Details',
                        )
                    },
                })
        }

        if (this.bioDetailsFormGroup.dirty) {
            this.callerService
                .updateProfileBio(this.bioDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated updateProfileBio', res)
                    },
                    error: (err) => {
                        this.callerService.showNotification(
                            'Failed to update Bio Details',
                        )
                    },
                })
        }

        if (this.ppaDetailsFormGroup.dirty) {
            this.callerService
                .updateProfilePpaDetails(this.ppaDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated updateProfilePpaDetails', res)
                    },
                    error: (err) => {
                        this.callerService.showNotification(
                            'Failed to update PPA Details',
                        )
                    },
                })
        }

        if (this.otherDetailsFormGroup.dirty) {
            this.callerService
                .updateProfileOtherDetails(this.otherDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated otherDetailsFormGroup', res)
                    },
                    error: (err) => {
                        this.callerService.showNotification(
                            'Failed to update Other Details',
                        )
                    },
                })
        }
    }

    /**
     * Upgrade method from updateCorpMemberProfile().
     *
     * update with ~Promise.all~ forkJoin()
     * Create ngrx actions to cater for this.
     *
     * Missing personalDetailsFormGroup values
     */
    updateEverything(): void {
        // trying formgroup of formgroups
        console.log('profileFormGroup', this.profileFormGroup.value)

        const updateMethods = []

        if (this.otherDetailsFormGroup.dirty) {
            updateMethods.push(
                this.callerService.updateProfileOtherDetails(
                    this.otherDetailsFormGroup.value,
                ),
            )
        }

        if (this.ppaDetailsFormGroup.dirty) {
            updateMethods.push(
                this.callerService.updateProfilePpaDetails(
                    this.ppaDetailsFormGroup.value,
                ),
            )
        }

        if (this.bioDetailsFormGroup.dirty) {
            updateMethods.push(
                this.callerService.updateProfileBio(
                    this.bioDetailsFormGroup.value,
                ),
            )
        }

        if (this.serviceDetailsFormGroup.pristine) {
            updateMethods.push(
                this.callerService.updateProfileServiceDetails(
                    this.serviceDetailsFormGroup.value,
                ),
            )
        }

        forkJoin(updateMethods).subscribe((values) => {
            console.log('updateEverything res??', values)

            if (values.every((res) => res.status === HttpStatusCode.Ok)) {
                this.callerService.showNotification('Profile updated')
            } else {
                // TODO: Find the sections that didn't update, and show an appropriate message.
                this.callerService.showNotification('Profile update failed.')
            }
        })
    }

    /**
     * Upgrade from updateEverything().
     */
    updateProfile(): void {
        this.profileFormGroup.markAllAsTouched() // so errors are shown
        this.updateProfilePicture()

        const hasFormChanges = this.profileFormGroup.dirty

        const validPendingLinks = this.pendingLinksArray.controls
            .map(g => g.value as { label: string; url: string })
            .filter(v => v.label?.trim() && v.url?.trim())

        const originalLinksCount = this.callerService.corpMember?.ProfileLinks?.length ?? 0
        const hasLinkChanges = validPendingLinks.length > 0 || this.externalLinks.length !== originalLinksCount || this.hasEditedLinks

        if (!hasFormChanges && !hasLinkChanges) {
            return
        }

        if (!this.profileFormGroup.valid) {
            this.ppaDetailsFormGroup.markAsTouched()
            this.ppaDetailsFormGroup.updateValueAndValidity()
            this.callerService.showNotification('Fix errors in the form.')
            return
        }

        this.isUpdatingProfile = true

        // Send the full desired set: existing remaining + new valid ones
        const links = [
            ...this.externalLinks.map(l => ({ label: l.label, url: l.url })),
            ...validPendingLinks.map(l => ({ label: l.label, url: l.url })),
        ]

        this.sendProfile(links)
    }

    /**
     * Saves the profile. `confirmNewPpa`: they've seen the PPAs like the one they typed and theirs
     * is none of them, so the backend adds it.
     */
    private sendProfile(links: Array<{ label: string; url: string }>, confirmNewPpa = false): void {
        this.isUpdatingProfile = true
        this.callerService.updateProfile({
            ...this.serviceDetailsFormGroup.value,
            ...this.bioDetailsFormGroup.value,
            ...this.ppaDetailsFormGroup.value,
            ...this.otherDetailsFormGroup.value,
            ...this.personalDetailsFormGroup.value,
            links,
            ...(confirmNewPpa && { confirm_new_ppa: true }),
        }).subscribe({
            next: (res: HttpResponse<any>) => {
                if (res.status === HttpStatusCode.Ok) {

                    this.callerService.store.dispatch(
                        profileUpdateSuccess({ data: { ...res.body.result } }),
                    )

                    if (!this.ppaDetailsFormGroup.value.ppa_id && this.ppaDetailsFormGroup.value.ppa_name) {
                        // A typed PPA may have been added - list it next time.
                        this.ppaDirectory.forget(this.serviceDetailsFormGroup.get('lga_id')?.value ?? this.callerService.corpMember.lga_id)
                    }

                    this.externalLinks = res.body?.result?.ProfileLinks ?? []
                    this.pendingLinksArray.clear()
                    this.hasEditedLinks = false
                }

                this.callerService.showNotification('Profile updated')
                this.isUpdatingProfile = false
            },
            error: (err: HttpErrorResponse) => {
                this.isUpdatingProfile = false
                if (err?.error?.code === 'SIMILAR_PPAS') {
                    this.askWhichPpa(err.error.suggestions, links)
                    return
                }
                this.callerService.showNotification(err?.error?.message ?? 'Failed to update profile')
                if (err?.error?.code === 'PPA_REMOVED') {
                    // An admin deleted the PPA this form had - clear it so they pick one again.
                    this.ppaDetailsFormGroup.get('ppa_name')?.reset()
                    this.ppaDetailsFormGroup.get('ppa_id')?.reset()
                    return
                }
                if (err?.error?.code === 'PPA_NAME_BLOCKED') return
                Sentry.captureException(err.error)
            },
        })
    }

    /** The PPA they typed looks like ones listed: theirs is one of them (save with it), or none (add it). */
    private askWhichPpa(suggestions: SimilarPpa[], links: Array<{ label: string; url: string }>): void {
        const name = this.ppaDetailsFormGroup.get('ppa_name')?.value ?? ''
        this.callerService.askWhichPpa({ name, suggestions }).subscribe((result) => {
            if (!result) return
            if ('pick' in result) {
                this.ppaDetailsFormGroup.get('ppa_name')?.setValue(result.pick.name, { emitEvent: false })
                this.ppaDetailsFormGroup.get('ppa_id')?.setValue(result.pick.id, { emitEvent: false })
                this.ppaDetailsFormGroup.get('type_of_institution')?.setValue(result.pick.type_of_ppa)
                this.sendProfile(links)
            } else {
                this.sendProfile(links, true)
            }
        })
    }

    /** Their PPA was deleted by an admin: the profile still links it (for a restore), but it's gone. */
    get ppaRemoved(): boolean {
        const corper = this.callerService.corpMember
        return !!corper?.ppa_id && !corper?.PPA
    }

    // no abrupt return here.
    updateProfilePicture(): void {
        console.log('this.pp.has(pic)', this.pp.has('pic'))
        if (this.pp.has('pic')) {
            this.isUpdatingProfile = true
            this.callerService.updateProfilePicture(this.pp)
                .subscribe({
                    next: (res) => {
                    
                        if (res.body) {
                            const _res = JSON.parse(res.body)
                            console.log('pic res', _res)
                            const profilePicUrl = _res.urls?.[0]

                            this.callerService.store.dispatch(
                                profileUpdateSuccess({ data: { Media : _res.Media } }),
                            )
                        }

                        this.pp = new FormData() // reset
                        this.profilePhotoInput.nativeElement.value = '' // reset the input value

                        this.isUpdatingProfile = false

                        this.callerService.showNotification('Profile picture updated')
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log('pic ??? ERR', err)

                        this.isUpdatingProfile = false

                        this.callerService.showNotification('Failed to update profile picture')
                        Sentry.captureException(err.error);
                    }
                })
        }
        
    }

    pp: FormData = new FormData()
    selectedImage: string = ''

    externalLinks: ProfileLink[] = this.callerService.corpMember?.ProfileLinks || []

    readonly LINK_LABEL_OPTIONS = ['Instagram', 'LinkedIn', 'Twitter', 'Facebook']

    pendingLinksArray = new FormArray<FormGroup>([])

    editingLinkIds = new Set<number>()
    linkEditForms = new Map<number, FormGroup>()
    hasEditedLinks = false

    get pendingLinkGroups(): FormGroup[] {
        return this.pendingLinksArray.controls as FormGroup[]
    }

    addExternalLink(): void {
        this.pendingLinksArray.push(new FormGroup({
            label: new FormControl(''),
            url: new FormControl('', Validators.pattern(/^https?:\/\/.+/)),
        }))
    }

    removePendingLink(index: number): void {
        this.pendingLinksArray.removeAt(index)
    }

    deleteExternalLink(id: number): void {
        this.externalLinks = this.externalLinks.filter((l) => l.id !== id)
        this.cancelEditLink(id)
    }

    getLinkEditForm(id: number): FormGroup {
        return this.linkEditForms.get(id)!
    }

    startEditLink(link: ProfileLink): void {
        this.linkEditForms.set(link.id, new FormGroup({
            label: new FormControl(link.label),
            url: new FormControl(link.url, Validators.pattern(/^https?:\/\/.+/)),
        }))
        this.editingLinkIds.add(link.id)
    }

    cancelEditLink(id: number): void {
        this.editingLinkIds.delete(id)
        this.linkEditForms.delete(id)
    }

    saveEditLink(id: number): void {
        const form = this.getLinkEditForm(id)
        if (!form?.valid) return
        const { label, url } = form.value
        this.externalLinks = this.externalLinks.map((l) =>
            l.id === id ? { ...l, label, url } : l
        )
        this.hasEditedLinks = true
        this.cancelEditLink(id)
    }

    onFileSelected(event: Event): void {
        console.log('evt', event)

        const target = event.target as HTMLInputElement
        const file = target.files?.[0]

        console.log('UI', file)

        if (file) {
            const reader = new FileReader() // do we wanna define this every time the function is called?

            reader.onerror = function () {
                console.log('FileReader Err:', reader.error)
            }

            reader.onload = (e: any) => {
                console.log('e', e)
                this.selectedImage = e.target.result
            }

            reader.readAsDataURL(file)

            // `media-${file.lastModified}-${file.size}-${file.name}`
            this.pp.set('pic', file) // setting individually so it's easier to delete.

        }
    }



    get hasPublicProfile(): boolean {
        return isPublicProfileEnabled(this.callerService.corpMember)
    }

    get corpMemberSelectedLgaName(): string {
        return this.selected_state_lgas.find(
            (opt) =>
                opt.id === this.serviceDetailsFormGroup.get(['lga_id'])?.value,
        )?.name
    }

}
